import React, { useContext } from 'react';

import i18n from '../../i18n';
import PropTypes from 'prop-types';
import styles from './GuideTeaser.module.scss';
import classNames from 'classnames';
import { ucFirst } from '../../utils/caseconverters';
import Icon from '../Icon';
import { TrackingContext } from '../StreamField/StreamField';
import { trackBanner } from '../../utils/datalayer';
import { useTranslation } from 'next-i18next';

const GuideTeaser = ({
    title = '',
    preamble = '',
    image = {},
    size = 'normal',
    label = null,
    text = null,
    color = 'blue50',
    iconColor = 'black',
    href = '/',
    modifiers = [],
}) => {
    const bannerLevel = useContext(TrackingContext);
    const { t } = useTranslation();

    const classes = classNames(
        styles['GuideTeaser'],
        {
            [styles['GuideTeaser--Grid']]: size === 'large',
        },
        {
            [styles['GuideTeaser--Normal']]: size === 'normal',
        },
        modifiers.map((mod) => styles['GuideTeaser--' + mod])
    );

    return (
        <div className={classes}>
            <div className={styles['GuideTeaser__Wrapper']}>
                <div
                    className={classNames('', {
                        [styles['GuideTeaser--' + ucFirst(size)]]:
                            size === 'large',
                    })}>
                    {(title || preamble) && (
                        <div
                            className={classNames(
                                styles['GuideTeaser__Heading']
                            )}>
                            {title && (
                                <h2
                                    className={classNames(
                                        styles['GuideTeaser__Title']
                                    )}>
                                    {title}
                                </h2>
                            )}
                            {preamble && (
                                <p
                                    className={classNames(
                                        styles['GuideTeaser__Preamble']
                                    )}>
                                    {preamble}
                                </p>
                            )}
                        </div>
                    )}

                    {!!image.renditions && (
                        <div
                            className={classNames(
                                styles['GuideTeaser__Image'],
                                styles['GuideTeaser__Image--' + ucFirst(size)]
                            )}
                            style={{
                                backgroundImage: `url(${image.renditions.large.src})`,
                                backgroundPosition: `${image.focal.x} ${image.focal.y}`,
                            }}>
                            <a
                                href={href}
                                onClick={() => {
                                    if (bannerLevel) {
                                        trackBanner(bannerLevel, label, text);
                                    }
                                }}
                                className={styles['GuideTeaser__Link']}>
                                <span className="sr-only">
                                    {t('generic.categories') + ': '} {label},{' '}
                                    {text}
                                </span>
                            </a>

                            {text && (
                                <div
                                    className={classNames(
                                        styles['GuideTeaser__Button'],
                                        styles[
                                            'GuideTeaser__Button--' +
                                                ucFirst(color)
                                        ]
                                    )}>
                                    <strong
                                        className={
                                            styles['GuideTeaser__Label']
                                        }>
                                        {label}
                                    </strong>

                                    <div
                                        className={classNames(
                                            styles['GuideTeaser__ButtonText']
                                        )}>
                                        {text}

                                        <div
                                            className={
                                                styles[
                                                    'GuideTeaser__IconWrapper'
                                                ]
                                            }>
                                            <div
                                                className={
                                                    styles[
                                                        'GuideTeaser__InnerIconWrapper'
                                                    ]
                                                }>
                                                <Icon
                                                    type="arrow"
                                                    size="large"
                                                    color={iconColor}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

GuideTeaser.propTypes = {
    image: PropTypes.object.isRequired,
    size: PropTypes.oneOf(['normal', 'large']),
    label: PropTypes.string,
    text: PropTypes.string,
    color: PropTypes.oneOf([
        'blue50',
        'green50',
        'purple50',
        'orange50',
        'black800',
    ]),
    iconColor: PropTypes.oneOf(['black', 'white']),
    href: PropTypes.string,
    title: PropTypes.string,
    preamble: PropTypes.string,
    modifiers: PropTypes.array,
};

export default GuideTeaser;
